import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SendIcon from '@mui/icons-material/Send';
import {
    Box,
    Button,
    Divider,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import React, { useMemo } from 'react';

export type ManagedUserInvite = {
    sentBy?: string;
    sentAt?: string | Date;
    accepted?: boolean;
    expired?: boolean;
    expiresAt?: string | Date;
    url?: string;
    inviteLink?: string;
};

function formatSentDateOnly(sentAt: string | Date | undefined) {
    if (sentAt == null || sentAt === '') return '—';
    return moment(sentAt).format('D MMM YYYY');
}

function formatSentTimeLine(sentAt: string | Date | undefined) {
    if (sentAt == null || sentAt === '') return '';
    return `at ${moment(sentAt).format('h:mm A')}`;
}

function inviteExpiryEndMoment(invite: ManagedUserInvite | null | undefined) {
    if (!invite?.sentAt) return null;
    return invite.expiresAt
        ? moment(invite.expiresAt)
        : moment(invite.sentAt).add(7, 'days');
}

function formatInviteExpiryDate(invite: ManagedUserInvite | null | undefined) {
    const end = inviteExpiryEndMoment(invite);
    return end ? end.format('D MMM YYYY') : '—';
}

function formatInviteExpiryRelative(
    invite: ManagedUserInvite | null | undefined,
) {
    const end = inviteExpiryEndMoment(invite);
    if (!end) return '';
    const days = Math.ceil(end.diff(moment(), 'days', true));
    if (days < 0) return 'Expired';
    if (days === 0) return 'today';
    return `in ${days} day${days === 1 ? '' : 's'}`;
}

export default function ManagedUserLoginSecurityPanel({
    email,
    emailEditSlot,
    lastLoginAt,
    invite,
    canSendInvite,
    onSendOrResendInvite,
    sendInviteDisabled,
}: {
    email?: string | null;
    /** When provided, shown instead of read-only email (inline edit). */
    emailEditSlot?: React.ReactNode;
    lastLoginAt?: string | Date | null;
    invite?: ManagedUserInvite | null;
    canSendInvite: boolean;
    onSendOrResendInvite: () => void;
    sendInviteDisabled?: boolean;
}) {
    const theme = useTheme();
    const isNarrow = useMediaQuery('(max-width: 700px)');

    const pending =
        Boolean(invite?.sentAt) &&
        !invite?.accepted &&
        !invite?.expired &&
        !lastLoginAt;

    const isEditingEmail = emailEditSlot != null;

    const invitationStatus = useMemo(() => {
        const accepted = invite?.accepted === true || Boolean(lastLoginAt);

        if (accepted) {
            return {
                statusLabel: 'Accepted',
                helper:
                    invite?.accepted === true
                        ? 'The user has accepted their invitation.'
                        : 'This account has signed in at least once.',
            };
        }
        if (pending) {
            return {
                statusLabel: 'Pending',
                helper: '',
            };
        }
        if (invite?.expired) {
            return {
                statusLabel: 'Expired',
                helper: 'The previous invitation has expired. Send a new invite.',
            };
        }

        const helper = canSendInvite
            ? 'No invitation has been sent to this email yet.'
            : '—';

        return {
            statusLabel: 'Not sent',
            helper,
        };
    }, [
        lastLoginAt,
        pending,
        invite?.accepted,
        invite?.expired,
        canSendInvite,
    ]);

    const inviteSendLabel =
        Boolean(invite?.sentAt) ||
        Boolean(invite?.expired) ||
        Boolean(invite?.accepted)
            ? 'Resend invite'
            : 'Send invite';

    const inviteSendButton =
        !pending && canSendInvite ? (
            <Button
                type='button'
                variant='contained'
                size='medium'
                startIcon={<SendIcon sx={{ fontSize: 18 }} />}
                disabled={Boolean(sendInviteDisabled)}
                onClick={onSendOrResendInvite}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    flexShrink: 0,
                    bgcolor: theme.palette.dark.main,
                    '&:hover': {
                        boxShadow: 'none',
                        bgcolor: alpha(theme.palette.dark.main, 0.88),
                    },
                }}
            >
                {inviteSendLabel}
            </Button>
        ) : null;

    const smallMetaIconCircle = (icon: React.ReactNode) => (
        <Box
            sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: (t) => alpha(t.palette.grey[500], 0.12),
                color: 'text.secondary',
                '& .MuiSvgIcon-root': {
                    fontSize: 16,
                    width: 16,
                    height: 16,
                },
            }}
        >
            {icon}
        </Box>
    );

    const emailBlock = (
        <Stack
            spacing={0.5}
            sx={{
                minWidth: 0,
                width: '100%',
                ...(isEditingEmail
                    ? { maxWidth: 320, alignSelf: 'flex-start' }
                    : { flex: 1 }),
            }}
        >
            <Typography
                variant='caption'
                component={isEditingEmail ? 'label' : 'span'}
                id={isEditingEmail ? 'managed-user-email-label' : undefined}
                htmlFor={isEditingEmail ? 'managed-user-email' : undefined}
                color='text.secondary'
                sx={{ fontWeight: 500, letterSpacing: 0.02 }}
            >
                Email address
            </Typography>
            {emailEditSlot != null ? (
                <Box sx={{ mt: 0.25, width: '100%' }}>{emailEditSlot}</Box>
            ) : (
                <Typography
                    variant='body2'
                    sx={{
                        color: 'text.primary',
                        fontWeight: 400,
                        wordBreak: 'break-word',
                    }}
                >
                    {email?.trim() ? email : '—'}
                </Typography>
            )}
        </Stack>
    );

    const statusBlock = (
        <Stack
            direction={isNarrow ? 'column' : 'row'}
            alignItems={isNarrow ? 'stretch' : 'center'}
            justifyContent='space-between'
            spacing={isNarrow ? 1.5 : 2}
            sx={{ height: '100%', width: '100%', minWidth: 0, flex: 1 }}
        >
            <Stack
                direction='row'
                spacing={1}
                alignItems='flex-start'
                sx={{ minWidth: 0, flex: 1 }}
            >
                {smallMetaIconCircle(<MailOutlineOutlinedIcon />)}
                <Stack
                    spacing={0.25}
                    alignItems='flex-start'
                    sx={{ minWidth: 0 }}
                >
                    <Typography variant='body2'>Invitation</Typography>
                    <Typography variant='body2' color='text.primary'>
                        {invitationStatus.statusLabel}
                    </Typography>
                    {invitationStatus.helper ? (
                        <Typography variant='body2' color='text.secondary'>
                            {invitationStatus.helper}
                        </Typography>
                    ) : null}
                </Stack>
            </Stack>
            {inviteSendButton ? (
                <Box
                    sx={{
                        flexShrink: 0,
                        alignSelf: isNarrow ? 'flex-end' : 'auto',
                    }}
                >
                    {inviteSendButton}
                </Box>
            ) : null}
        </Stack>
    );

    const sentTimeSubline = formatSentTimeLine(invite?.sentAt);
    const expiryRelativeSubline = formatInviteExpiryRelative(invite);

    const invitationDetailsRuleHorizontal = (
        <Box
            sx={{
                height: '1px',
                alignSelf: 'stretch',
                flexShrink: 0,
                bgcolor: theme.palette.custom.modalActionBorder,
            }}
        />
    );

    const invitationColumnOnly = (
        <Stack
            direction='row'
            spacing={1}
            alignItems='flex-start'
            sx={{ minWidth: 0 }}
        >
            {smallMetaIconCircle(<MailOutlineOutlinedIcon />)}
            <Stack spacing={0.25} alignItems='flex-start' sx={{ minWidth: 0 }}>
                <Typography variant='body2'>Invitation</Typography>
                <Typography variant='body2' color='text.primary'>
                    {invitationStatus.statusLabel}
                </Typography>
                {invitationStatus.helper ? (
                    <Typography variant='body2' color='text.secondary'>
                        {invitationStatus.helper}
                    </Typography>
                ) : null}
            </Stack>
        </Stack>
    );

    const sentByColumn = (
        <Stack
            direction='row'
            spacing={1}
            alignItems='flex-start'
            sx={{ minWidth: 0 }}
        >
            {smallMetaIconCircle(<PersonOutlineIcon />)}
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant='body2'>Sent by</Typography>
                <Typography variant='body2' color='text.primary'>
                    {invite?.sentBy?.trim() ? invite.sentBy : '—'}
                </Typography>
            </Stack>
        </Stack>
    );

    const pendingColumnSx = (columnIndex: number) => ({
        minWidth: 0,
        ...(!isNarrow && columnIndex > 0
            ? {
                  borderLeft: `1px solid ${theme.palette.custom.modalActionBorder}`,
                  pl: 2,
              }
            : {}),
    });

    const sentOnBlock = (
        <Stack
            direction='row'
            spacing={1}
            alignItems='flex-start'
            sx={{ minWidth: 0 }}
        >
            {smallMetaIconCircle(<CalendarTodayOutlinedIcon />)}
            <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                <Typography variant='body2'>Sent on</Typography>
                <Typography variant='body2' color='text.primary'>
                    {formatSentDateOnly(invite?.sentAt)}
                </Typography>
                {sentTimeSubline ? (
                    <Typography variant='body2' color='text.secondary'>
                        {sentTimeSubline}
                    </Typography>
                ) : null}
            </Stack>
        </Stack>
    );

    const expiresBlock = (
        <Stack
            direction='row'
            spacing={1}
            alignItems='flex-start'
            sx={{ minWidth: 0 }}
        >
            {smallMetaIconCircle(<AccessTimeIcon />)}
            <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                <Typography variant='body2'>Expires</Typography>
                <Typography variant='body2' color='text.primary'>
                    {formatInviteExpiryDate(invite)}
                </Typography>
                {expiryRelativeSubline ? (
                    <Typography variant='body2' color='text.secondary'>
                        {expiryRelativeSubline}
                    </Typography>
                ) : null}
            </Stack>
        </Stack>
    );

    const whatsNextBlock = (
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography variant='body2'>What&apos;s next?</Typography>
            <Typography variant='body2' color='text.primary'>
                The user will receive an email with instructions to activate
                their account.
            </Typography>
        </Stack>
    );

    const pendingDetailsColumns = [
        invitationColumnOnly,
        sentByColumn,
        sentOnBlock,
        expiresBlock,
        whatsNextBlock,
    ];

    const pendingBanner =
        pending && !isEditingEmail ? (
            <Paper
                elevation={0}
                sx={{
                    mt: 2.5,
                    p: 0,
                    borderRadius: 0,
                    border: 'none',
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                }}
            >
                <Stack spacing={0}>
                    {isNarrow ? (
                        <Stack spacing={0}>
                            {pendingDetailsColumns.map((cell, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 ? (
                                        <Box sx={{ py: 1.5 }}>
                                            {invitationDetailsRuleHorizontal}
                                        </Box>
                                    ) : null}
                                    <Box sx={pendingColumnSx(0)}>{cell}</Box>
                                </React.Fragment>
                            ))}
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                width: '100%',
                                alignItems: 'stretch',
                                gridTemplateColumns:
                                    'repeat(5, minmax(0, 1fr))',
                            }}
                        >
                            {pendingDetailsColumns.map((cell, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        ...pendingColumnSx(index),
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        minHeight: 0,
                                    }}
                                >
                                    {cell}
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Stack
                        direction='row'
                        justifyContent='flex-end'
                        alignItems='center'
                        sx={{ pt: 2 }}
                    >
                        <Button
                            type='button'
                            variant='contained'
                            size='medium'
                            startIcon={<SendIcon sx={{ fontSize: 18 }} />}
                            disabled={Boolean(sendInviteDisabled)}
                            onClick={onSendOrResendInvite}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: 'none',
                                bgcolor: theme.palette.dark.main,
                                '&:hover': {
                                    boxShadow: 'none',
                                    bgcolor: alpha(
                                        theme.palette.dark.main,
                                        0.88,
                                    ),
                                },
                            }}
                        >
                            Resend invite
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        ) : null;

    const emailStatusRow = isEditingEmail ? (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-start',
            }}
        >
            {emailBlock}
        </Box>
    ) : isNarrow ? (
        pending ? (
            <Box sx={{ width: '100%' }}>{emailBlock}</Box>
        ) : (
            <Stack spacing={2}>
                {emailBlock}
                <Divider />
                {statusBlock}
            </Stack>
        )
    ) : pending ? (
        <Box sx={{ width: '100%' }}>{emailBlock}</Box>
    ) : (
        <Stack direction='row' spacing={0} alignItems='stretch'>
            <Box sx={{ flex: 1, minWidth: 0 }}>{emailBlock}</Box>
            <Box
                sx={{
                    width: '1px',
                    alignSelf: 'stretch',
                    flexShrink: 0,
                    bgcolor: theme.palette.custom.modalActionBorder,
                    mx: 3,
                }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>{statusBlock}</Box>
        </Stack>
    );

    return (
        <Stack spacing={0}>
            {emailStatusRow}
            {pendingBanner}
        </Stack>
    );
}
